import React, { useEffect } from "react";
import "../../../assets/css/customer/Full Story/FullStory.css";
import MyNavbar from "../../../components/customer/navigationbar/MyNavbar";
import Footer from "../../../subscreen/customer/footer/Footer";

const milestones = [
  {
    year: "The Beginning",
    title: "A Dream Takes Shape",
    desc: "AD Furniture House was born from a simple belief — every home deserves furniture that tells a story. From a small workshop in Daska, Punjab, we started crafting pieces with our own hands.",
    icon: "🪵",
  },
  {
    year: "Our Craft",
    title: "Quality Above All",
    desc: "We source the finest wood, fabric, and materials to ensure every sofa, bed, dining set, and cabinet we make stands the test of time. Our craftsmen bring decades of experience to every joint and finish.",
    icon: "🔨",
  },
  {
    year: "Our Growth",
    title: "From Daska to Every Home",
    desc: "Word spread fast. Families across Punjab trusted AD Furniture House to furnish their homes. We grew our catalog to include every type of furniture — bedroom, living room, office, and outdoor.",
    icon: "🏠",
  },
  {
    year: "Today",
    title: "Your Home, Our Passion",
    desc: "Today, AD Furniture House is more than a store. It's a promise — that when you bring our furniture home, you're bringing craftsmanship, love, and quality with it.",
    icon: "✨",
  },
];

const values = [
  {
    title: "Craftsmanship",
    desc: "Every piece is handcrafted with care and attention to detail.",
    icon: "🪚",
  },
  {
    title: "Quality",
    desc: "We use only premium materials that last for generations.",
    icon: "⭐",
  },
  {
    title: "Trust",
    desc: "Thousands of families in Punjab trust our name.",
    icon: "🤝",
  },
  {
    title: "Innovation",
    desc: "Classic designs with modern comfort and style.",
    icon: "💡",
  },
];

const FullStory = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".fs-animate").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <MyNavbar />
      <div className="fs-root">

        {/* ── HERO ── */}
        <section className="fs-hero">
          <div className="fs-hero-img" />
          <div className="fs-hero-content">
            <h1 className="fs-hero-title">
              Bringing&nbsp;Craftsmanship<br />
              Into Every Home
            </h1>
          </div>
         
        </section>

        {/* ── WHO WE ARE ── */}
        <section className="fs-who">
          <div className="fs-who-inner">
            {/* stats tile */}
            <div className="fs-stats-tile fs-animate">
              <div className="fs-stat">
                <div className="fs-stat-num">100+</div>
                <div className="fs-stat-label">Furniture Designs</div>
              </div>
              <div className="fs-stat">
                <div className="fs-stat-num">40+</div>
                <div className="fs-stat-label">Years of Craft</div>
              </div>
              <div className="fs-stat">
                <div className="fs-stat-num">1000+</div>
                <div className="fs-stat-label">Homes Furnished</div>
              </div>
            </div>

            {/* text */}
            <div className="fs-who-text fs-animate fs-animate-delay-1">
              <h2>Who <em>We Are</em></h2>
              <p>
                Welcome to <strong>AD Furniture House</strong> — a family-owned
                furniture brand rooted in the heart of Daska, Punjab. We believe
                that a beautiful home starts with beautiful furniture, and
                beautiful furniture starts with honest craftsmanship.
              </p>
              <p>
                From elegant sofas and cozy beds to sturdy dining tables and
                stylish wardrobes, every piece we create carries the spirit of
                our hometown and the dedication of our skilled artisans.
              </p>
            </div>

            {/* image */}
            <div className="fs-who-img fs-animate fs-animate-delay-2" />
          </div>
        </section>

        {/* ── OUR JOURNEY (milestones as numbered steps) ── */}
        <section className="fs-journey">
          <div className="fs-journey-header fs-animate">
            <h2>How We <em>Built</em> Our Story</h2>
            <p>From a small workshop to the homes of thousands</p>
          </div>
          <div className="fs-steps-grid">
            {milestones.map((m, i) => (
              <div key={i} className="fs-step fs-animate" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="fs-step-num">{i + 1}</div>
                <div className="fs-step-body">
                  <span className="fs-step-label">{m.year}</span>
                  <h3>{m.title}</h3>
                  <p>{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── VALUES — bento grid ── */}
        <section className="fs-values">
          <div className="fs-values-header fs-animate">
            <h2>Why <em>Choose</em> AD Furniture</h2>
            <p>The principles that guide every piece we make</p>
          </div>
          <div className="fs-bento">
            {/* first 3 as regular cards */}
            {values.slice(0, 3).map((v, i) => (
              <div
                key={i}
                className={`fs-bento-card fs-animate ${i === 2 ? "dark" : ""}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="fs-bento-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}

            {/* last value as wide card with image */}
            <div className="fs-bento-card wide fs-animate fs-animate-delay-2">
              <div className="fs-bento-img" />
              <div className="fs-bento-text">
                <div className="fs-bento-icon">{values[3].icon}</div>
                <h3>{values[3].title}</h3>
                <p>{values[3].desc}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="fs-cta fs-animate">
          <div className="fs-cta-inner">
            <h2>
              Come Visit Us in <em>Daska</em>
            </h2>
            <p>
              Walk into our showroom and experience the craftsmanship yourself.
              Our team is always ready to help you find the perfect piece for
              your home.
            </p>
            <div className="fs-cta-btns">
              <a href="/products" className="fs-btn">View Products</a>
              <a href="/contact-us" className="fs-btn fs-btn-outline">Contact Us</a>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
};

export default FullStory;